import { useContext, useEffect, useRef, useState } from 'react';
import { WebApiContext } from 'ascendon-web-api';
import { translateFault, generateUnhandledFault } from '../../helpers/faults';
import fetchIntercept from 'fetch-intercept';

function useCreateSubscriberWithSecurityApi() {
  const [fault, setFault] = useState(null);
  const [uiModel, setUiModel] = useState(null);
  const [faultCode, setFaultCode] = useState(null);
  const WebApi = useContext(WebApiContext);

  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const getHeaders = ({ apiKey, headersToRemove }) => {
    const generalRequestHeaders = {
      name: 'Content-Type',
      value: 'application/json; charset=utf-8',
    };
    const setApiKey = apiKey?.length
      ? {
          add: [
            { name: 'apikey', value: apiKey },
            { ...generalRequestHeaders },
          ],
        }
      : {
          add: [{ ...generalRequestHeaders }],
        };
    const headers = {
      ...setApiKey,
      remove: headersToRemove,
    };
    return headers;
  };

  const getApiParams = ({ securityApiKey, securityApiUrl, postBody }) => {
    const baseURL = `${securityApiUrl}`;
    const updateByPassword = '/account/subscriber/authenticate/by-password';
    const { Credentials } = postBody;
    const payload = {
      Login: Credentials.Login,
      Password: Credentials.Password,
      DeviceType: ASCENDON_ENV_CONFIG.deviceType,
      DistributionChannel: ASCENDON_ENV_CONFIG.channelId,
      language: ASCENDON_APP_CONFIG.language,
    };
    const headers = getHeaders({
      apiKey: securityApiKey,
      headersToRemove: [
        'CD-DeviceType',
        'CD-DistributionChannel',
        'CD-SystemId',
        'CD-SessionId',
        'CD-Language',
      ],
    });
    const securityApiByPasswordUrl = `${baseURL}${updateByPassword}`;

    return {
      securityApiByPasswordUrl,
      payload,
      headers,
    };
  };

  const setFaultActivity = (error, setFaultState, setFaultCodeState) => {
    const code = error?.data?.Code;
    let errorMsg;
    if (!code) {
      const unknownFault = generateUnhandledFault(error.data);
      errorMsg = translateFault(unknownFault).translatedMessage;
      setFaultCodeState(translateFault(error.data)?.faultCode || 'Unknown');
      setFaultState(errorMsg);
    } else if (code === 113) {
      setFaultCodeState(code);
      setFaultState('code_113');
    } else {
      errorMsg = translateFault(error.data).translatedMessage;
      setFaultCodeState(translateFault(error.data)?.faultCode || 'Unknown');
      setFaultState(errorMsg);
    }
  };

  const createUIModel = (createSubscriberResponse, authenticationResponse) => {
    const overrideData = authenticationResponse?.data
      ? authenticationResponse?.data
      : createSubscriberResponse.data;
    const overridePasswordIsTemporary =
      authenticationResponse?.PasswordIsTemporary !== undefined
        ? authenticationResponse?.PasswordIsTemporary
        : createSubscriberResponse.PasswordIsTemporary;
    const overrideSessionId = authenticationResponse?.SessionId
      ? authenticationResponse?.SessionId
      : createSubscriberResponse.SessionId;
    const overrideCountry = authenticationResponse?.Country
      ? authenticationResponse?.Country
      : createSubscriberResponse?.Subscriber?.Country ||
        createSubscriberResponse?.Subscriber?.HomeCountry;
    const result = {
      ...createSubscriberResponse,
      data: overrideData,
      PasswordIsTemporary: overridePasswordIsTemporary,
      SessionId: overrideSessionId,
      Country: overrideCountry,
    };
    return result;
  };

  const requestCreateSubscriberWithSecurityApi = ({
    subscriberPayload,
    securityApiKey,
    securityApiUrl,
    staticApiKey,
    staticApiUrl,
    emulateApigeeCall,
  }) => {
    setFault(null);
    const isProduction =
      ASCENDON_ENV_CONFIG?.environment?.toLowerCase().indexOf('prod') === 1;

    if (emulateApigeeCall && !isProduction) {
      const createSubscriberHeaders = getHeaders({
        headersToRemove: ['CD-SessionId'],
      });
      WebApi.subscriber({
        forceRefresh: true,
        callName: 'CreateSubscriber',
        keyFactors: ['CreateSubscriber'],
        payload: subscriberPayload,
        headers: createSubscriberHeaders,
      })
        .then((subscriberResult) => {
          if (isMounted.current) {
            setUiModel(createUIModel(subscriberResult?.data, undefined));
          }
        })
        .catch((error) => {
          console.warn({ error });
          if (isMounted.current) {
            setFaultActivity(error, setFault, setFaultCode);
          }
        });
    } else {
      let unregister = fetchIntercept.register({
        request: function (url, config) {
          if (config) {
            config.credentials = 'include';
          }
          return [url, config];
        },
      });
      const createSubscriberApiKey = staticApiKey;
      const createSubscriberUrl = `${staticApiUrl}/account/Subscriber/CreateSubscriber`;
      const createSubscriberHeaders = getHeaders({
        apiKey: createSubscriberApiKey,
        headersToRemove: ['CD-SessionId'],
      });

      WebApi.subscriber({
        forceRefresh: true,
        payload: subscriberPayload,
        customUrl: createSubscriberUrl,
        headers: createSubscriberHeaders,
      })
        .then((subscriberResult) => {
          if (isMounted.current) {
            const { securityApiByPasswordUrl, payload, headers } = getApiParams(
              {
                securityApiUrl,
                securityApiKey,
                postBody: subscriberPayload,
              },
            );

            WebApi.subscriber({
              forceRefresh: true,
              payload,
              headers,
              customUrl: securityApiByPasswordUrl,
            })
              .then((authenticationResponse) => {
                if (unregister) {
                  unregister();
                }

                if (isMounted.current) {
                  setUiModel(
                    createUIModel(
                      subscriberResult?.data,
                      authenticationResponse?.data,
                    ),
                  );
                }
              })
              .catch((error) => {
                if (unregister) {
                  unregister();
                }

                console.warn({ error });
                if (isMounted.current) {
                  setFaultActivity(error, setFault, setFaultCode);
                }
              });
          }
        })
        .catch((error) => {
          if (unregister) {
            unregister();
          }
          console.warn({ error });
          if (isMounted.current) {
            setFaultActivity(error, setFault, setFaultCode);
          }
        });
    }
  };
  return [uiModel, fault, faultCode, requestCreateSubscriberWithSecurityApi];
}

export { useCreateSubscriberWithSecurityApi };
export default useCreateSubscriberWithSecurityApi;
